/**
 * Verodel – logo-voorbeeld voor clubs op de offertepagina (templates/page.custom-paddles.liquid,
 * snippets/vd-club-preview.liquid).
 *
 * De editor (masker, slepen, zoomen, draaien, achtergrond, tekenen) is dezelfde als op de
 * productpagina (assets/custom-paddle-preview.js). Die code staat hier als PaddlePreviewBase,
 * zodat de productpagina een eigen, ongewijzigd bestand houdt. Pas je de editor daar aan,
 * neem de wijziging dan ook hier over.
 *
 * Werking clubversie
 * - Eigen uploadknop (en slepen) in het blok. Er wordt niets geüpload: alles blijft in de browser.
 * - Editor met keuze tussen de vormen (Standaard / Rond). De plaatsing is relatief aan het
 *   paddleblad, dus die gaat mee bij het wisselen van vorm.
 * - Resultaat: alle vormen naast elkaar, delen (telefoon) of downloaden als één JPG,
 *   en een notitie in het offerteformulier (veld contact[Logo-voorbeeld]).
 */

const PREVIEWABLE = ['png', 'jpg', 'jpeg', 'svg'];
const MAX_SOURCE_SIDE = 2400;
const SVG_RENDER_SIDE = 2000;
const MIN_SCALE = 0.05;
const MAX_SCALE = 6;
const SNAP = 0.02;
const OUTPUT_TYPE = 'image/jpeg';
const OUTPUT_QUALITY = 0.9;
const DEFAULT_BG = '#ffffff';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

function boxFromPath(d) {
  const nums = (d.match(/-?\d*\.?\d+(?:e-?\d+)?/gi) || []).map(Number);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const x = nums[i], y = nums[i + 1];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function loadImage(url, crossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Afbeelding kon niet worden geladen.'));
    img.src = url;
  });
}

function isCrossOrigin(url) {
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/** Zorgt dat een SVG een intrinsieke maat heeft, anders tekenen sommige browsers hem op 0×0. */
function prepareSvgText(text) {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const svg = doc.documentElement;
  if (!svg || svg.nodeName.toLowerCase() !== 'svg' || doc.querySelector('parsererror')) {
    throw new Error('Ongeldige SVG');
  }
  let w = parseFloat(svg.getAttribute('width') || '');
  let h = parseFloat(svg.getAttribute('height') || '');
  const vb = (svg.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
  const hasVb = vb.length === 4 && vb[2] > 0 && vb[3] > 0;
  const percent = /%/.test(svg.getAttribute('width') || '') || /%/.test(svg.getAttribute('height') || '');
  if (!w || !h || percent) {
    if (hasVb) {
      w = vb[2];
      h = vb[3];
    } else {
      w = w || 1000;
      h = h || 1000;
    }
  }
  const ratio = SVG_RENDER_SIDE / Math.max(w, h);
  if (!hasVb) svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  svg.setAttribute('width', String(Math.round(w * ratio)));
  svg.setAttribute('height', String(Math.round(h * ratio)));
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  return new XMLSerializer().serializeToString(svg);
}

async function loadSource(file, ext) {
  let url;
  if (ext === 'svg') {
    const text = prepareSvgText(await file.text());
    url = URL.createObjectURL(new Blob([text], { type: 'image/svg+xml' }));
  } else {
    url = URL.createObjectURL(file);
  }
  try {
    const img = await loadImage(url, false);
    const nw = img.naturalWidth || img.width;
    const nh = img.naturalHeight || img.height;
    if (!nw || !nh) throw new Error('Geen afmetingen');
    const ratio = Math.min(1, MAX_SOURCE_SIDE / Math.max(nw, nh));
    const w = Math.max(1, Math.round(nw * ratio));
    const h = Math.max(1, Math.round(nh * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, w, h);
    let hasAlpha = false;
    if (ext !== 'jpg' && ext !== 'jpeg') {
      const probe = document.createElement('canvas');
      probe.width = 64;
      probe.height = 64;
      const pctx = probe.getContext('2d', { willReadFrequently: true });
      pctx.drawImage(canvas, 0, 0, 64, 64);
      const data = pctx.getImageData(0, 0, 64, 64).data;
      let transparent = 0;
      for (let i = 3; i < data.length; i += 4) if (data[i] < 245) transparent++;
      hasAlpha = transparent > 40;
    }
    return { canvas, w, h, hasAlpha };
  } finally {
    URL.revokeObjectURL(url);
  }
}

class PaddlePreviewBase extends HTMLElement {
  /* ---------------- melding ---------------- */

  _showNote(text) {
    if (!this.note) return;
    this.note.textContent = text || this.defaultNote;
    this.note.hidden = false;
  }

  _hideNote() {
    if (this.note) this.note.hidden = true;
  }

  /* ---------------- bestand ---------------- */

  async _onFileChange() {
    const token = ++this.token;
    // Eerst andere change-handlers laten lopen (op de productpagina leegt de configurator een ongeldig bestand).
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (token !== this.token) return;
    const file = this.fileInput.files?.[0];
    if (!file) {
      this._reset();
      return;
    }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!PREVIEWABLE.includes(ext)) {
      this._reset();
      this._showNote();
      return;
    }
    this._hideNote();
    this._setPanelBusy(true);
    try {
      const [source] = await Promise.all([loadSource(file, ext), this._ensureBase()]);
      if (token !== this.token) return;
      this.source = source;
      this.fileName = file.name;
      this.state = { x: 0, y: 0, s: 1, r: 0, bg: DEFAULT_BG };
      this._fit(source.hasAlpha || ext === 'svg' ? 'contain' : 'cover');
      this._syncBgButtons();
      this._setPanelBusy(false);
      await this._commit();
      if (token !== this.token) return;
      this.open();
    } catch (error) {
      if (token !== this.token) return;
      console.warn('[custom-paddle-preview]', error);
      this._reset();
      this._showNote(
        this.errorText || 'We konden van dit bestand geen voorbeeld maken. Je kunt het gewoon bestellen: wij verwerken je logo in het ontwerp.'
      );
    }
  }

  _removeFile() {
    this.fileInput.value = '';
    this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    if (this.dialog.open) this._closeDialog(false);
    this.fileInput.focus({ preventScroll: true });
  }

  /* ---------------- geometrie ---------------- */

  /** Afmetingen van het (gedraaide) logo bij schaal 1 = breedte paddleblad. */
  _rotatedUnit() {
    const aspect = this.source.h / this.source.w;
    const w = this.face.w;
    const h = this.face.w * aspect;
    return this.state.r % 180 === 0 ? { w, h } : { w: h, h: w };
  }

  /* ---------------- tekenen ---------------- */

  /** Tekent de complete mockup (foto + logo in het paddleblad) op ctx, geschaald met factor k. */
  _drawComposite(ctx, k) {
    const W = this.baseW * k;
    const H = this.baseH * k;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();
    const t = ctx.getTransform();
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.base, 0, 0, W, H);

    ctx.save();
    ctx.scale(W, H);
    ctx.clip(this.facePath);
    ctx.setTransform(t);
    ctx.fillStyle = this.state.bg;
    ctx.fillRect(0, 0, W, H);
    this._drawDesign(ctx, (this.face.cx + this.state.x * this.face.w) * k, (this.face.cy + this.state.y * this.face.w) * k, k, 1);
    ctx.restore();

    // Subtiele binnenschaduw langs de rand van het blad voor diepte.
    ctx.save();
    ctx.scale(W, H);
    ctx.clip(this.facePath);
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = Math.max(2, this.face.w * k * 0.02) * (t.a || 1);
    ctx.fillStyle = '#000';
    ctx.fill(this.ringPath, 'evenodd');
    ctx.restore();
  }

  _drawDesign(ctx, cx, cy, k, alpha) {
    const dw = this.state.s * this.face.w * k;
    const dh = dw * (this.source.h / this.source.w);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(cx, cy);
    ctx.rotate((this.state.r * Math.PI) / 180);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.source.canvas, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  }

  _stageMetrics() {
    const rect = this.stage.getBoundingClientRect();
    const w = Math.max(1, rect.width);
    const h = Math.max(1, rect.height);
    const m = Math.min((w * 0.78) / this.face.w, (h * 0.86) / this.face.h);
    return { w, h, m, cx: w / 2, cy: h / 2 };
  }

  _renderStage() {
    if (!this.stage || !this.source || !this.face) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const { w, h, m, cx, cy } = this._stageMetrics();
    const cw = Math.round(w * dpr);
    const ch = Math.round(h * dpr);
    if (this.stage.width !== cw || this.stage.height !== ch) {
      this.stage.width = cw;
      this.stage.height = ch;
    }
    const ctx = this.stage.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#e9e9e9';
    ctx.fillRect(0, 0, w, h);

    const dx = cx + this.state.x * this.face.w * m;
    const dy = cy + this.state.y * this.face.w * m;

    // Logo buiten het blad: gedimd zichtbaar.
    this._drawDesign(ctx, dx, dy, m, 0.32);

    // Paddleblad
    const pathTransform = [this.baseW * m, 0, 0, this.baseH * m, cx - this.face.cx * m, cy - this.face.cy * m];
    ctx.save();
    ctx.transform(...pathTransform);
    ctx.clip(this.facePath);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = this.state.bg;
    ctx.fillRect(0, 0, w, h);
    this._drawDesign(ctx, dx, dy, m, 1);
    ctx.restore();

    // Rand van het blad
    ctx.save();
    ctx.transform(...pathTransform);
    ctx.lineJoin = 'round';
    ctx.lineWidth = 4 / (this.baseW * m);
    ctx.strokeStyle = 'rgba(255,255,255,0.95)';
    ctx.stroke(this.facePath);
    ctx.lineWidth = 2 / (this.baseW * m);
    ctx.strokeStyle = '#1f6feb';
    ctx.stroke(this.facePath);
    ctx.restore();

    // Hulplijnen bij centreren
    if (this.gesture && (this.snapped.x || this.snapped.y)) {
      ctx.save();
      ctx.strokeStyle = 'rgba(31,111,235,0.85)';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      if (this.snapped.x) {
        ctx.moveTo(cx, cy - (this.face.h * m) / 2);
        ctx.lineTo(cx, cy + (this.face.h * m) / 2);
      }
      if (this.snapped.y) {
        const yLine = cy + 0;
        ctx.moveTo(cx - (this.face.w * m) / 2, yLine);
        ctx.lineTo(cx + (this.face.w * m) / 2, yLine);
      }
      ctx.stroke();
      ctx.restore();
    }
  }

  _renderMockup() {
    if (!this.mockup || !this.source || !this.base) return;
    const rect = this.mockup.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const size = Math.max(1, Math.round(rect.width * dpr));
    const ratio = this.baseH / this.baseW;
    if (this.mockup.width !== size) {
      this.mockup.width = size;
      this.mockup.height = Math.round(size * ratio);
    }
    const ctx = this.mockup.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    this._drawComposite(ctx, size / this.baseW);
  }

  _requestRender() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this._renderStage();
      this._renderMockup();
    });
  }

  /* ---------------- dialoog ---------------- */

  _bindDialog() {
    const d = this.dialog;
    this.stage = d.querySelector('[data-vd-pv-stage]');
    this.mockup = d.querySelector('[data-vd-pv-mockup]');
    this.zoomRange = d.querySelector('[data-vd-pv-zoom-range]');
    this.bgButtons = [...d.querySelectorAll('[data-vd-pv-bg]')];
    this.bgCustom = d.querySelector('[data-vd-pv-bg-custom]');

    d.querySelectorAll('[data-vd-pv-done]').forEach((btn) => btn.addEventListener('click', () => this._closeDialog(true)));
    d.querySelectorAll('[data-vd-pv-remove]').forEach((btn) => btn.addEventListener('click', () => this._removeFile()));
    d.addEventListener('cancel', (event) => {
      event.preventDefault();
      this._closeDialog(true);
    });
    d.addEventListener('click', (event) => {
      if (event.target === d) this._closeDialog(true);
    });

    d.querySelectorAll('[data-vd-pv-zoom]').forEach((btn) =>
      btn.addEventListener('click', () => this._zoomBy(Number(btn.dataset.vdPvZoom) > 0 ? 1.15 : 1 / 1.15))
    );
    this.zoomRange?.addEventListener('input', () => {
      if (!this.state) return;
      const v = Number(this.zoomRange.value) / 1000;
      const target = MIN_SCALE * Math.pow(MAX_SCALE / MIN_SCALE, v);
      this._zoomBy(target / this.state.s, null, true);
    });
    d.querySelectorAll('[data-vd-pv-rotate]').forEach((btn) =>
      btn.addEventListener('click', () => {
        if (!this.state) return;
        this.state.r = (this.state.r + Number(btn.dataset.vdPvRotate) + 360) % 360;
        this._announce();
        this._requestRender();
      })
    );
    d.querySelectorAll('[data-vd-pv-fit]').forEach((btn) =>
      btn.addEventListener('click', () => {
        if (!this.state) return;
        this._fit(btn.dataset.vdPvFit);
        this._requestRender();
      })
    );
    this.bgButtons.forEach((btn) =>
      btn.addEventListener('click', () => {
        if (!this.state) return;
        this.state.bg = btn.dataset.vdPvBg || DEFAULT_BG;
        this._syncBgButtons();
        this._requestRender();
      })
    );
    this.bgCustom?.addEventListener('input', () => {
      if (!this.state) return;
      this.state.bg = this.bgCustom.value;
      this._syncBgButtons();
      this._requestRender();
    });

    // Interactie op het canvas
    const stage = this.stage;
    stage.addEventListener('pointerdown', (e) => this._onPointerDown(e));
    stage.addEventListener('pointermove', (e) => this._onPointerMove(e));
    stage.addEventListener('pointerup', (e) => this._onPointerUp(e));
    stage.addEventListener('pointercancel', (e) => this._onPointerUp(e));
    stage.addEventListener('lostpointercapture', (e) => this._onPointerUp(e));
    stage.addEventListener('wheel', (e) => this._onWheel(e), { passive: false });
    stage.addEventListener('keydown', (e) => this._onKey(e));

    this.resizeObserver = new ResizeObserver(() => {
      if (this.dialog.open) this._requestRender();
    });
    this.resizeObserver.observe(stage);
    if (this.mockup) this.resizeObserver.observe(this.mockup);
  }

  open() {
    if (!this.source || !this.state || this.dialog.open) return;
    this.lastFocus = document.activeElement;
    document.documentElement.classList.add('vd-pv-open');
    this.dialog.showModal();
    this._syncZoomRange();
    this._syncBgButtons();
    this._requestRender();
    requestAnimationFrame(() => this.stage?.focus({ preventScroll: true }));
  }

  async _closeDialog(commit) {
    if (this.dialog.open) this.dialog.close();
    document.documentElement.classList.remove('vd-pv-open');
    this.pointers.clear();
    this.gesture = null;
    if (commit && this.source) await this._commit();
    const back = this.panel && !this.panel.hidden ? this.panel.querySelector('[data-vd-pv-edit]') : this.lastFocus;
    if (back instanceof HTMLElement) back.focus({ preventScroll: true });
  }

  _syncZoomRange() {
    if (!this.zoomRange || !this.state) return;
    const v = Math.log(this.state.s / MIN_SCALE) / Math.log(MAX_SCALE / MIN_SCALE);
    this.zoomRange.value = String(Math.round(clamp(v, 0, 1) * 1000));
  }

  _syncBgButtons() {
    if (!this.state) return;
    let matched = false;
    this.bgButtons.forEach((btn) => {
      const on = (btn.dataset.vdPvBg || '').toLowerCase() === this.state.bg.toLowerCase();
      if (on) matched = true;
      btn.setAttribute('aria-pressed', String(on));
    });
    const custom = this.bgCustom?.closest('.vd-pv-swatch');
    if (custom) {
      custom.classList.toggle('is-active', !matched);
      if (!matched && this.bgCustom) this.bgCustom.value = this.state.bg;
    }
  }

  _announce() {
    this._syncZoomRange();
  }

  /* ---------------- interactie ---------------- */

  _zoomBy(factor, point, fromRange) {
    if (!this.state || !this.face) return;
    const next = clamp(this.state.s * factor, MIN_SCALE, MAX_SCALE);
    const real = next / this.state.s;
    if (point) {
      // Punt onder de cursor blijft op zijn plek.
      const { m, cx, cy } = this._stageMetrics();
      const dx = cx + this.state.x * this.face.w * m;
      const dy = cy + this.state.y * this.face.w * m;
      const ndx = point.x + (dx - point.x) * real;
      const ndy = point.y + (dy - point.y) * real;
      this.state.x = (ndx - cx) / (this.face.w * m);
      this.state.y = (ndy - cy) / (this.face.w * m);
    } else {
      this.state.x *= real;
      this.state.y *= real;
    }
    this.state.s = next;
    if (!fromRange) this._syncZoomRange();
    this._requestRender();
  }

  _localPoint(e) {
    const rect = this.stage.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  _onPointerDown(e) {
    if (!this.state) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    this.stage.focus({ preventScroll: true });
    try {
      this.stage.setPointerCapture?.(e.pointerId);
    } catch {
      /* synthetische of al losgelaten pointer */
    }
    this.pointers.set(e.pointerId, this._localPoint(e));
    this._startGesture();
  }

  _startGesture() {
    const pts = [...this.pointers.values()];
    if (!pts.length) {
      this.gesture = null;
      return;
    }
    const mid = pts.length > 1 ? { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 } : pts[0];
    const dist = pts.length > 1 ? Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) : 0;
    this.gesture = { mid, dist, x: this.state.x, y: this.state.y, s: this.state.s, raw: { x: this.state.x, y: this.state.y } };
    this.stage.classList.add('is-dragging');
  }

  _onPointerMove(e) {
    if (!this.gesture || !this.pointers.has(e.pointerId)) return;
    e.preventDefault();
    this.pointers.set(e.pointerId, this._localPoint(e));
    const pts = [...this.pointers.values()];
    const { m, cx, cy } = this._stageMetrics();
    const unit = this.face.w * m;
    const g = this.gesture;
    const mid = pts.length > 1 ? { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 } : pts[0];

    let s = g.s;
    if (pts.length > 1 && g.dist > 0) {
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      s = clamp(g.s * (dist / g.dist), MIN_SCALE, MAX_SCALE);
    }
    const real = s / g.s;
    // Positie: verschuiving van het middelpunt + schalen rond het beginmiddelpunt.
    const startCx = cx + g.x * unit;
    const startCy = cy + g.y * unit;
    const ncx = g.mid.x + (startCx - g.mid.x) * real + (mid.x - g.mid.x);
    const ncy = g.mid.y + (startCy - g.mid.y) * real + (mid.y - g.mid.y);
    let x = (ncx - cx) / unit;
    let y = (ncy - cy) / unit;

    this.snapped = { x: Math.abs(x) < SNAP, y: Math.abs(y) < SNAP };
    if (this.snapped.x) x = 0;
    if (this.snapped.y) y = 0;

    this.state.x = clamp(x, -3, 3);
    this.state.y = clamp(y, -3, 3);
    if (s !== this.state.s) {
      this.state.s = s;
      this._syncZoomRange();
    }
    this._requestRender();
  }

  _onPointerUp(e) {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.delete(e.pointerId);
    if (this.pointers.size) {
      this._startGesture();
    } else {
      this.gesture = null;
      this.snapped = { x: false, y: false };
      this.stage.classList.remove('is-dragging');
      this._requestRender();
    }
  }

  _onWheel(e) {
    if (!this.state) return;
    e.preventDefault();
    const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    this._zoomBy(Math.exp(-delta * 0.0015), this._localPoint(e));
  }

  _onKey(e) {
    if (!this.state) return;
    const step = e.shiftKey ? 0.05 : 0.01;
    let handled = true;
    switch (e.key) {
      case 'ArrowLeft':
        this.state.x -= step;
        break;
      case 'ArrowRight':
        this.state.x += step;
        break;
      case 'ArrowUp':
        this.state.y -= step;
        break;
      case 'ArrowDown':
        this.state.y += step;
        break;
      case '+':
      case '=':
        this._zoomBy(1.1);
        break;
      case '-':
      case '_':
        this._zoomBy(1 / 1.1);
        break;
      case 'r':
      case 'R':
        this.state.r = (this.state.r + (e.shiftKey ? 270 : 90)) % 360;
        break;
      default:
        handled = false;
    }
    if (handled) {
      e.preventDefault();
      this._requestRender();
    }
  }
}

/* =====================================================================
 * Clubversie: <custom-paddle-club-preview> op de offertepagina (vanaf 5 paddles).
 * - Eigen uploadknop (en slepen) in het blok. Er wordt niets geüpload: alles blijft in de browser.
 * - Editor met keuze tussen de vormen (Standaard / Rond). De plaatsing is relatief aan het
 *   paddleblad, dus die gaat mee bij het wisselen van vorm.
 * - Resultaat: alle vormen naast elkaar, delen (telefoon) of downloaden als één JPG,
 *   en een notitie in het offerteformulier (veld contact[Logo-voorbeeld]).
 * ===================================================================== */

const SHARE_TILE = 800;
const SHARE_BAND = 150;

class CustomPaddleClubPreview extends PaddlePreviewBase {
  connectedCallback() {
    if (this.ready || typeof Path2D === 'undefined') return;
    this.dialog = this.querySelector('[data-vd-pv-dialog]');
    this.fileInput = this.querySelector('[data-vd-club-file]');
    this.shapes = [...this.querySelectorAll('[data-vd-club-shape]')]
      .map((figure) => {
        let path = '';
        try {
          path = JSON.parse(figure.querySelector('[data-vd-club-path]')?.textContent || '""');
        } catch {
          path = '';
        }
        if (!path) return null;
        const facePath = new Path2D(path);
        const ringPath = new Path2D();
        ringPath.rect(-1, -1, 3, 3);
        ringPath.addPath(facePath);
        const img = figure.querySelector('[data-vd-club-img]');
        return {
          name: figure.dataset.name || '',
          baseUrl: figure.dataset.baseUrl || '',
          facePath,
          ringPath,
          faceN: boxFromPath(path),
          img,
          origSrc: img?.getAttribute('src') || '',
          origSrcset: img?.getAttribute('srcset') || '',
          origAlt: img?.getAttribute('alt') || '',
          photo: null,
        };
      })
      .filter(Boolean);
    if (!this.dialog || !this.fileInput || !this.shapes.length) return;
    this.ready = true;

    // De galerijfuncties van de productpagina zoeken binnen this.section: hier is dat alleen dit blok.
    this.section = this;
    this.panel = null;
    this.active = 0;
    this.source = null;
    this.state = null;
    this.token = 0;
    this.pointers = new Map();
    this.gesture = null;
    this.snapped = { x: false, y: false };
    this.frame = 0;
    this.composites = [];
    this.urls = [];
    this.facePath = this.shapes[0].facePath;
    this.ringPath = this.shapes[0].ringPath;
    this.faceN = this.shapes[0].faceN;

    this.note = this.querySelector('[data-vd-club-note]');
    this.defaultNote = this.note?.textContent.trim() || '';
    this.errorText = this.dataset.errorText || '';
    this.status = this.querySelector('[data-vd-club-status]');
    this.startActions = this.querySelector('[data-vd-club-start]');
    this.doneActions = this.querySelector('[data-vd-club-done]');
    this.shareBtn = this.querySelector('[data-vd-club-share]');
    this.drop = this.querySelector('[data-vd-club-drop]');

    // Delen via het deelmenu van de telefoon (WhatsApp e.d.), anders downloaden.
    try {
      const probe = new File([new Blob(['x'])], 'x.jpg', { type: OUTPUT_TYPE });
      this.canShareFiles =
        window.matchMedia('(pointer: coarse)').matches && !!navigator.share && !!navigator.canShare?.({ files: [probe] });
    } catch {
      this.canShareFiles = false;
    }
    if (this.canShareFiles && this.shareBtn?.dataset.shareLabel) this.shareBtn.textContent = this.shareBtn.dataset.shareLabel;

    this._bindDialog();
    this._bindShapeToggle();
    this._bindBlock();
    this._bindForm();

    this.fileInput.addEventListener('change', () => this._onFileChange());
    if (this.fileInput.files?.length) this._onFileChange();
  }

  /* ---------------- vormen ---------------- */

  async _ensureBase() {
    await Promise.all(
      this.shapes.map(async (shape) => {
        if (shape.photo) return;
        const url = shape.baseUrl.startsWith('//') ? window.location.protocol + shape.baseUrl : shape.baseUrl;
        const img = await loadImage(url, isCrossOrigin(url));
        shape.photo = img;
        shape.w = img.naturalWidth;
        shape.h = img.naturalHeight;
        shape.face = {
          x: shape.faceN.x * shape.w,
          y: shape.faceN.y * shape.h,
          w: shape.faceN.w * shape.w,
          h: shape.faceN.h * shape.h,
        };
        shape.face.cx = shape.face.x + shape.face.w / 2;
        shape.face.cy = shape.face.y + shape.face.h / 2;
      })
    );
    this._useShape(this.active);
    return this.base;
  }

  _useShape(index) {
    const shape = this.shapes[index] || this.shapes[0];
    this.active = this.shapes.indexOf(shape);
    this.facePath = shape.facePath;
    this.ringPath = shape.ringPath;
    this.faceN = shape.faceN;
    if (shape.photo) {
      this.base = shape.photo;
      this.baseW = shape.w;
      this.baseH = shape.h;
      this.face = shape.face;
    }
  }

  _withShape(index, fn) {
    const previous = this.active;
    this._useShape(index);
    try {
      return fn();
    } finally {
      this._useShape(previous);
    }
  }

  /** Passend: past op elke vorm. Vullend: vult elke vorm. */
  _fit(mode) {
    if (!this.source || !this.face) return;
    const scales = this.shapes.map((shape, i) =>
      shape.face
        ? this._withShape(i, () => {
            const unit = this._rotatedUnit();
            return mode === 'cover'
              ? Math.max(this.face.w / unit.w, this.face.h / unit.h)
              : 0.72 * Math.min(this.face.w / unit.w, (this.face.h * 0.8) / unit.h);
          })
        : NaN
    ).filter((v) => Number.isFinite(v));
    if (!scales.length) return;
    const s = mode === 'cover' ? Math.max(...scales) : Math.min(...scales);
    this.state.s = clamp(s, MIN_SCALE, MAX_SCALE);
    this.state.x = 0;
    this.state.y = mode === 'cover' ? 0 : -0.06 * (this.face.h / this.face.w);
    this._syncZoomRange();
  }

  _bindShapeToggle() {
    this.shapeButtons = [...this.dialog.querySelectorAll('[data-vd-club-shape-btn]')];
    this.shapeButtons.forEach((btn) =>
      btn.addEventListener('click', () => {
        this._useShape(Number(btn.dataset.vdClubShapeBtn) || 0);
        if (this.mockup) this.mockup.width = 0;
        this._syncShapeButtons();
        this._requestRender();
      })
    );
  }

  _syncShapeButtons() {
    this.shapeButtons?.forEach((btn) => {
      btn.setAttribute('aria-pressed', String((Number(btn.dataset.vdClubShapeBtn) || 0) === this.active));
    });
  }

  open() {
    this._syncShapeButtons();
    if (this.mockup) this.mockup.width = 0;
    super.open();
  }

  /* ---------------- blok op de pagina ---------------- */

  _bindBlock() {
    this.querySelectorAll('[data-vd-club-tile]').forEach((tile) =>
      tile.addEventListener('click', () => {
        if (this.source && this.state) {
          this._useShape(Number(tile.dataset.vdClubTile) || 0);
          this.open();
        } else {
          this.fileInput.click();
        }
      })
    );
    this.querySelectorAll('[data-vd-club-edit]').forEach((btn) => btn.addEventListener('click', () => this.open()));
    this.querySelectorAll('[data-vd-club-replace]').forEach((btn) => btn.addEventListener('click', () => this.fileInput.click()));
    this.querySelectorAll('[data-vd-club-remove]').forEach((btn) => btn.addEventListener('click', () => this._removeFile()));
    this.shareBtn?.addEventListener('click', () => this._share());
    this.querySelectorAll('[data-vd-club-quote]').forEach((link) =>
      link.addEventListener('click', () => {
        window.setTimeout(() => document.getElementById('vd-naam')?.focus({ preventScroll: true }), 450);
      })
    );

    if (this.drop) {
      const over = (event) => {
        if (![...(event.dataTransfer?.types || [])].includes('Files')) return;
        event.preventDefault();
        this.drop.classList.add('is-dragover');
      };
      this.drop.addEventListener('dragenter', over);
      this.drop.addEventListener('dragover', over);
      this.drop.addEventListener('dragleave', (event) => {
        if (!this.drop.contains(event.relatedTarget)) this.drop.classList.remove('is-dragover');
      });
      this.drop.addEventListener('drop', (event) => {
        const file = event.dataTransfer?.files?.[0];
        this.drop.classList.remove('is-dragover');
        if (!file) return;
        event.preventDefault();
        try {
          const transfer = new DataTransfer();
          transfer.items.add(file);
          this.fileInput.files = transfer.files;
          this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
        } catch {
          /* oude browser: gebruik de knop */
        }
      });
    }
  }

  _say(text) {
    if (this.status) this.status.textContent = text;
  }

  _setPanelBusy(busy) {
    this.classList.toggle('is-busy', busy);
    if (busy) this._say('Voorbeeld maken…');
  }

  _removeFile() {
    this.token++;
    this.fileInput.value = '';
    this._reset();
    this.fileInput.focus({ preventScroll: true });
    this._say('Logo verwijderd.');
  }

  _revokeUrls() {
    this.urls.forEach((url) => URL.revokeObjectURL(url));
    this.urls = [];
  }

  _reset() {
    this.source = null;
    this.state = null;
    this._revokeUrls();
    this.composites = [];
    this.shapes.forEach((shape) => {
      if (!shape.img) return;
      if (shape.origSrcset) shape.img.setAttribute('srcset', shape.origSrcset);
      shape.img.setAttribute('src', shape.origSrc);
      shape.img.setAttribute('alt', shape.origAlt);
    });
    this.classList.remove('has-preview', 'is-busy');
    if (this.startActions) this.startActions.hidden = false;
    if (this.doneActions) this.doneActions.hidden = true;
    this._hideNote();
    this._updateForm();
    if (this.dialog.open) this._closeDialog(false);
  }

  async _commit() {
    if (!this.source || !this.state || !this.shapes.every((shape) => shape.photo)) return;
    const token = this.token;
    const results = [];
    for (let i = 0; i < this.shapes.length; i++) {
      const shape = this.shapes[i];
      const canvas = document.createElement('canvas');
      canvas.width = shape.w;
      canvas.height = shape.h;
      this._withShape(i, () => this._drawComposite(canvas.getContext('2d'), 1));
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, OUTPUT_TYPE, OUTPUT_QUALITY));
      if (!blob || token !== this.token || !this.source) {
        results.forEach((r) => URL.revokeObjectURL(r.url));
        return;
      }
      results.push({ canvas, url: URL.createObjectURL(blob) });
    }
    this._revokeUrls();
    this.composites = results.map((r) => r.canvas);
    this.urls = results.map((r) => r.url);
    this.shapes.forEach((shape, i) => {
      if (!shape.img) return;
      shape.img.removeAttribute('srcset');
      shape.img.setAttribute('src', this.urls[i]);
      shape.img.setAttribute('alt', `${shape.name}: voorbeeld van jullie logo op de paddle`);
    });
    this.classList.remove('is-busy');
    this.classList.add('has-preview');
    if (this.startActions) this.startActions.hidden = true;
    if (this.doneActions) this.doneActions.hidden = false;
    this._updateForm();
    this._say('Voorbeeld klaar.');
  }

  /** Kleine uitsnede rond elk paddleblad, naast elkaar (voor het formulier). */
  _thumb(size) {
    const n = this.composites.length;
    const canvas = document.createElement('canvas');
    canvas.width = size * n;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#f2f2f2';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    this.composites.forEach((src, i) => {
      const face = this.shapes[i].face;
      const side = Math.max(face.w, face.h) * 1.12;
      ctx.drawImage(src, face.cx - side / 2, face.cy - side / 2, side, side, i * size, 0, size, size);
    });
    return canvas.toDataURL('image/jpeg', 0.85);
  }

  async _shareBlob() {
    const tiles = this.composites.map((src) => ({ src, h: Math.round(SHARE_TILE * (src.height / src.width)) }));
    const tileH = Math.max(...tiles.map((t) => t.h));
    const canvas = document.createElement('canvas');
    canvas.width = SHARE_TILE * tiles.length;
    canvas.height = tileH + SHARE_BAND;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingQuality = 'high';
    tiles.forEach((t, i) => ctx.drawImage(t.src, i * SHARE_TILE, (tileH - t.h) / 2, SHARE_TILE, t.h));
    ctx.textAlign = 'center';
    ctx.fillStyle = '#111111';
    ctx.font = '700 30px Helvetica, Arial, sans-serif';
    this.shapes.forEach((shape, i) => ctx.fillText(shape.name.toUpperCase(), i * SHARE_TILE + SHARE_TILE / 2, tileH + 52, SHARE_TILE - 40));
    ctx.fillStyle = '#6b6b6b';
    ctx.font = '400 22px Helvetica, Arial, sans-serif';
    ctx.fillText(
      this.dataset.shareCaption || 'Voorbeeld van de plaatsing. Het definitieve ontwerp maakt Verodel in jullie clubkleuren. verodel.com',
      canvas.width / 2,
      tileH + 108,
      canvas.width - 60
    );
    return new Promise((resolve) => canvas.toBlob(resolve, OUTPUT_TYPE, OUTPUT_QUALITY));
  }

  async _share() {
    if (!this.composites.length) return;
    const blob = await this._shareBlob();
    if (!blob) return;
    const file = new File([blob], 'verodel-logo-voorbeeld.jpg', { type: OUTPUT_TYPE });
    if (this.canShareFiles) {
      try {
        await navigator.share({
          files: [file],
          title: 'Ons logo op een Verodel paddle',
          text: `Voorbeeld van de plaatsing: ${window.location.origin}${window.location.pathname}`,
        });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.hidden = true;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  /* ---------------- offerteformulier ---------------- */

  _bindForm() {
    this.attach = document.querySelector('[data-vd-club-attach]');
    this.attachInput = this.attach?.querySelector('[data-vd-club-attach-input]');
    this.attachImg = this.attach?.querySelector('[data-vd-club-attach-img]');
    this.attach?.querySelectorAll('[data-vd-club-attach-edit]').forEach((btn) => btn.addEventListener('click', () => this.open()));
  }

  _updateForm() {
    if (!this.attach || !this.attachInput) return;
    const on = !!(this.source && this.state && this.composites.length);
    this.attach.hidden = !on;
    this.attachInput.disabled = !on;
    if (!on) {
      this.attachInput.value = '';
      this.attachImg?.removeAttribute('src');
      return;
    }
    this.attachInput.value = `Ja, gemaakt op de site (bestand: ${this.fileName || 'onbekend'}, achtergrondkleur: ${String(this.state.bg).toUpperCase()})`;
    if (this.attachImg) this.attachImg.src = this._thumb(120);
  }
}

if (!customElements.get('custom-paddle-club-preview')) {
  customElements.define('custom-paddle-club-preview', CustomPaddleClubPreview);
}
